import { DtoExt } from '@/classes/scenario/DtoExt';
import { InstallationTypeRef, SurfaceDefinition, SurfaceTypeRef } from '@/dto/scenario/pv/SurfaceDefinition';

export type References = {
  readonly lostAreaReferences: readonly LostAreaReference[],
  readonly installationTypeReferences: readonly InstallationTypeReference[]
  systemUnavailabilityReferences: SystemUnavailabilityReference[]
}

type LostAreaReference = Readonly<Record<'area' | SurfaceTypeRef, number>>;
type SystemUnavailabilityReference = Readonly<Record<'pvPlantPower' | InstallationTypeRef, number>>;

type InstallationTypeReference =
  { readonly groundCoverageRatio: number } &
  { readonly [key in InstallationTypeRef]: Recommendations };

const Recommendations = ['valid', 'disadvised', 'disabled'] as const;
type Recommendations = typeof Recommendations[number];

const greatestFirstOn = <KEY extends PropertyKey, SORTED_TYPE extends Record<KEY, number>>
  (key: KEY) => ({ [key]: val1 }: SORTED_TYPE, { [key]: val2 }: SORTED_TYPE) => val2 - val1;

export class SurfaceDefinitionExt extends DtoExt {
  private readonly lostAreaReferences: LostAreaReference[];
  private readonly installationTypeReferences: InstallationTypeReference[];
  private systemUnavailabilityReferences: SystemUnavailabilityReference[];
  public surfaceTypeRef: SurfaceTypeRef | null;
  public completeAvailableArea: number | null;
  public areaOfExclusion: number | null;
  public moduleEfficiency: number | null;
  public pvPlantPower: number | null;
  public installationTypeRef: InstallationTypeRef | null;
  public yieldFromProspect: number | null;
  public forcedBifacialModule: boolean | null;
  public albedoFromProspect: number | null;
  public gridUnavailability: number | null;
  public otherLosses: number | null;

  constructor(options: Partial<SurfaceDefinitionExt> = {}, references?: References) {
    super();
    this.lostAreaReferences = [...references?.lostAreaReferences ?? []].sort(greatestFirstOn('area'));
    this.installationTypeReferences = [...references?.installationTypeReferences ?? []].sort(greatestFirstOn('groundCoverageRatio'));
    this.systemUnavailabilityReferences = [...references?.systemUnavailabilityReferences ?? []].sort(greatestFirstOn('pvPlantPower'));
    this.surfaceTypeRef = options.surfaceTypeRef ?? null;
    this.completeAvailableArea = options.completeAvailableArea ?? null;
    this.areaOfExclusion = options.areaOfExclusion ?? null;
    this.moduleEfficiency = options.moduleEfficiency ?? null;
    this.pvPlantPower = options.pvPlantPower ?? null;
    this.installationTypeRef = options.installationTypeRef ?? null;
    this.yieldFromProspect = options.yieldFromProspect ?? null;
    this.forcedBifacialModule = options.forcedBifacialModule ?? null;
    this.albedoFromProspect = options.albedoFromProspect ?? null;
    this.otherLosses = options.otherLosses ?? null;
    this.gridUnavailability = options.gridUnavailability ?? 0.005;
  }

  public installationTypeRecommendation(installationType: InstallationTypeRef): Recommendations | null {
    if (this.groundCoverageRatio === null) {
      return null;
    }
    const currentGroundCoverageRatio = this.groundCoverageRatio;
    return this.installationTypeReferences
      .find(({ groundCoverageRatio }) => currentGroundCoverageRatio >= groundCoverageRatio)
      ?.[installationType] ?? null;
  }

  public computePvPlantPower(groundCoverageRatio: number): number | null {
    if (this.moduleEfficiency === null || this.netUsableArea === null) {
      return null;
    }
    return this.moduleEfficiency * groundCoverageRatio * this.netUsableArea * 10 * 1000;
  }

  get lostArea(): number | null {
    if (this.totalAvailableArea === null || this.surfaceTypeRef === null) {
      return null;
    }
    const area = this.totalAvailableArea;
    return this.lostAreaReferences
      .find(({ area: referenceArea }) => area >= referenceArea)
      ?.[this.surfaceTypeRef] ?? null;
  }

  get netUsableArea(): number | null {
    if (this.lostArea === null || this.totalAvailableArea === null) {
      return null;
    }
    return this.totalAvailableArea * (1 - this.lostArea);
  }

  get groundCoverageRatio(): number | null {
    if (this.moduleEfficiency === null || this.pvPlantPower === null || this.netUsableArea === null) {
      return null;
    }
    return this.pvPlantPower / (this.netUsableArea * this.moduleEfficiency * 10 * 1000);
  }

  get computedBifacialModule() {
    return this.groundCoverageRatio! <= 0.7;
  }

  get bifacialModule() {
    if (this.groundCoverageRatio === null) {
      return this.forcedBifacialModule;
    }
    if (this.forcedBifacialModule === null) {
      return this.computedBifacialModule;
    }
    return this.forcedBifacialModule;
  }

  set bifacialModule(forcedBifacialModule : boolean | null) {
    this.forcedBifacialModule = forcedBifacialModule;
  }

  get relativeColumnSpacing(): number | null {
    if (this.groundCoverageRatio === null) {
      return null;
    }
    return 1 / this.groundCoverageRatio;
  }

  get bifacialGain(): number | null {
    if (this.bifacialModule === false) {
      return 0;
    }
    if (this.albedoFromProspect === null || this.groundCoverageRatio === null) {
      return null;
    }
    return 0.011 + this.albedoFromProspect / 5 + 0.054 * (0.4 - this.groundCoverageRatio);
  }

  get systemUnavailability(): number | null {
    const capacityPv = this.pvPlantPower;
    if (capacityPv === null || this.installationTypeRef === null) {
      return null;
    }
    return this.systemUnavailabilityReferences
      .find(({ pvPlantPower }) => capacityPv >= pvPlantPower)
      ?.[this.installationTypeRef] ?? null;
  }

  get annualYieldResults() {
    if (this.yieldFromProspect === null || this.bifacialGain === null || this.systemUnavailability === null || this.gridUnavailability === null || this.otherLosses === null) {
      return null;
    }
    return this.yieldFromProspect * (1 + this.bifacialGain) * (1 - this.systemUnavailability) * (1 - this.gridUnavailability) * (1 - this.otherLosses);
  }

  get annualEnergy() {
    if (this.pvPlantPower === null || this.annualYieldResults === null) {
      return null;
    }
    return this.pvPlantPower * this.annualYieldResults;
  }

  private get totalAvailableArea(): number | null {
    if (this.completeAvailableArea === null || this.areaOfExclusion === null) {
      return null;
    }
    return this.completeAvailableArea - this.areaOfExclusion;
  }

  public toDto(): SurfaceDefinition {
    return new SurfaceDefinition({
      surfaceTypeRef: this.surfaceTypeRef,
      completeAvailableArea: this.completeAvailableArea,
      areaOfExclusion: this.areaOfExclusion,
      lostArea: this.lostArea,
      netUsableArea: this.netUsableArea,
      moduleEfficiency: this.moduleEfficiency,
      pvPlantPower: this.pvPlantPower,
      groundCoverageRatio: this.groundCoverageRatio,
      relativeColumnSpacing: this.relativeColumnSpacing,
      installationTypeRef: this.installationTypeRef,
      yieldFromProspect: this.yieldFromProspect,
      bifacialModule: this.bifacialModule,
      albedoFromProspect: this.albedoFromProspect,
      bifacialGain: this.bifacialGain,
      systemUnavailability: this.systemUnavailability,
      gridUnavailability: this.gridUnavailability,
      otherLosses: this.otherLosses
    });
  }

  public static fromDto(dto: SurfaceDefinition): SurfaceDefinitionExt {
    return new SurfaceDefinitionExt(dto);
  }
}
